import { useEffect, useCallback, useMemo } from 'react';

import * as Dfns from 'date-fns/fp';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { FilterBarTokens } from '@blisspointmedia/bpm-types/dist/FilterBar';

import { Tooltip, Form, OverlayTrigger } from 'react-bootstrap';

import { Size } from '@Components/designConstants';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';

import { DagLambdaFetch, awaitJSON, MiscLambdaFetch } from '../../utils/fetch-utils';

import { BPMTable, TypedHeader } from '../../Components/StickyTable';
import { Page } from '../../Components/Page';
import { Spinner } from '../../Components/Spinner';
import { Button } from '../../Components/Button';
import { TableLoading } from '../../Components/TableEmptyState';
import { TablePage } from '../../Components/TablePage';

import { DEFAULT_ADVANCED_TOKENS } from './batch-failures-constants';
import { parseFilterTokens, withFilterParam } from './batch-failures-utils';
import { BATCH_FAILURES_QUERY_KEY, FailureRow, useBatchFailures } from './use-batch-failures';

import './BatchFailures.scss';

interface UpdateFailuresArgs {
  id: string;
  investigator?: string;
  investigation_time?: string;
  investigation_notes?: string;
  update?: boolean;
  restarting?: boolean;
  running?: boolean;
}

const PreviousBuilds = ({ data }: { data: FailureRow }) => (
  <div data-testid={`previous-builds-${data.job_id}`}>
    {data.builds.map(build => (
      <OverlayTrigger
        key={build.created}
        placement='left'
        overlay={
          <Tooltip id={build.created}>
            Status: {build.status}
            <br />
            Last Modified: {build.lastmodified}
          </Tooltip>
        }
      >
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={build.log_url}
          className='previous-build'
        >
          {build.status}
        </a>
      </OverlayTrigger>
    ))}
  </div>
);

const BatchFailures = () => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  const email = useAppSelector(UserRedux.emailSelector);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const isNOC = useAppSelector(UserRedux.isNOCSelector);
  const { data: failures, isLoading, error } = useBatchFailures();
  const [searchParams, setSearchParams] = useSearchParams();
  const advancedFilterTokens = useMemo(
    () => parseFilterTokens(searchParams) ?? DEFAULT_ADVANCED_TOKENS,
    [searchParams]
  );

  // Keeps the URL's ?filter param in sync as the user edits the advanced filter bar, so the
  // current URL is always a shareable link to the filter as it stands (see batch-failures-utils).
  // Only fires on an actual edit -- not on the initial mount from defaultTokens/sessionStorage --
  // so loading the page never rewrites a URL it was just given.
  const handleTokensChange = useCallback(
    (tokens: FilterBarTokens) => {
      if (!tokens.advanced) return;
      setSearchParams(current => withFilterParam(current, tokens.advanced ?? []), {
        replace: true
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to fetch batch failure info. Error: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Patches the query cache directly (rather than an editable local copy seeded from it once)
  // so a background refetch's fresh rows are never silently discarded -- the idiomatic React
  // Query form of "fetch, then locally mutate".
  const updateFailures = useCallback(
    (args: UpdateFailuresArgs) => {
      const {
        id,
        investigator,
        investigation_time,
        investigation_notes,
        update,
        restarting,
        running
      } = args;
      try {
        const patch: Partial<FailureRow> = {};
        if (investigator) {
          [patch.investigator] = investigator.split('@');
        }
        if (investigation_time) {
          patch.investigation_time = investigation_time;
        }
        if (investigation_notes) {
          patch.investigation_notes = investigation_notes;
        }
        if (restarting !== undefined) {
          patch.restarting = restarting;
        }
        if (running !== undefined) {
          patch.running = running;
        }

        let patchedRow: FailureRow | undefined;
        queryClient.setQueryData<FailureRow[]>(BATCH_FAILURES_QUERY_KEY, current =>
          current?.map(row => {
            if (row.job_id !== id) {
              return row;
            }
            patchedRow = { ...row, ...patch };
            return patchedRow;
          })
        );

        if (update && patchedRow) {
          MiscLambdaFetch('/update_batch_failures', {
            method: 'POST',
            body: patchedRow
          });
        }
      } catch (e) {
        setError({
          message: `Failed to update build info. Error: ${(e as Error).message}`,
          reportError: e as Error
        });
      }
    },
    [setError, queryClient]
  );

  const restartBuild = useCallback(
    async (args: { job_id: string; company: string; job_name: string }) => {
      const { job_id, company, job_name } = args;
      updateFailures({ id: job_id, restarting: false, running: true });
      try {
        const result = await DagLambdaFetch('/start', {
          method: 'POST',
          body: JSON.stringify({
            dag: company,
            start_task_name: job_name,
            override: isAdmin,
            retries: 0
          })
        });
        const parsedResult = await awaitJSON(result);
        if (!parsedResult.success) {
          setError({ message: parsedResult.message });
        }
      } catch (e) {
        setError({
          message: `Failed to restart build. Error: ${(e as Error).message}`,
          reportError: e as Error
        });
        updateFailures({ id: job_id, restarting: false, running: false });
      }
    },
    [setError, updateFailures, isAdmin]
  );

  const headers: TypedHeader<FailureRow>[] = [
    {
      label: 'Job Name',
      name: 'job_name',
      flex: 1,
      minFlexWidth: 250,
      renderer: data => <div className='job-name'>{data.job_name}</div>
    },
    { label: 'Last Modified', name: 'lastmodified', width: 200 },
    {
      label: 'Investigator',
      name: 'investigator',
      width: 150,
      renderer: data => (
        <div className='investigator-claim' data-testid={`investigator-${data.job_id}`}>
          {data.investigator && <div>{data.investigator}</div>}
        </div>
      )
    },
    {
      label: 'Investigation Time',
      name: 'investigation_time',
      width: 200,
      renderer: data => (data.investigation_time ? <div>{data.investigation_time}</div> : '')
    },
    {
      label: 'Investigation Notes',
      name: 'investigation_notes',
      width: 300,
      renderer: data => (
        <Form.Control
          data-testid={`notes-input-${data.job_id}`}
          value={data.investigation_notes || ''}
          onChange={e => {
            updateFailures({
              id: data.job_id,
              investigation_notes: e.target.value
            });
          }}
          onBlur={e => {
            if (e.target.value.trim().length > 0) {
              updateFailures({
                id: data.job_id,
                investigator: email,
                investigation_time: Dfns.format("yyyy-MM-dd'T'HH:mm:ss", new Date()),
                update: true
              });
            }
          }}
        />
      )
    },
    {
      label: 'Ignore',
      name: 'ignore',
      width: 80,
      renderer: data => (
        <Button
          size={Size.LG}
          data-testid={`ignore-button-${data.job_id}`}
          onClick={() =>
            updateFailures({
              id: data.job_id,
              investigator: email,
              investigation_time: Dfns.format("yyyy-MM-dd'T'HH:mm:ss", new Date()),
              investigation_notes: 'IGNORE',
              update: true
            })
          }
        >
          Ignore
        </Button>
      )
    },
    {
      label: 'Failure Logs',
      name: 'log_url',
      width: 100,
      renderer: data => (
        <div data-testid={`log-link-${data.job_id}`}>
          {data.log_url ? (
            <a href={data.log_url} target='_blank' rel='noopener noreferrer'>
              Link
            </a>
          ) : (
            ''
          )}
        </div>
      )
    },
    {
      label: 'Previous Builds',
      name: 'builds',
      flex: 1,
      minFlexWidth: 500,
      renderer: data => <PreviousBuilds data={data} />
    }
  ];

  const restartHeader: TypedHeader<FailureRow> = {
    label: 'Restartable',
    name: 'running',
    width: 80,
    renderer: data =>
      data.running ? (
        <div>
          <Spinner />
        </div>
      ) : (
        <Button
          size={Size.LG}
          data-testid={`restart-button-${data.job_id}`}
          onClick={() => restartBuild(data)}
          loading={data.restarting}
        >
          Restart
        </Button>
      )
  };

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        emptyMessage='No failures to show'
        rowHeight={50}
        headerHeight={50}
      />
    ),
    [isLoading, error]
  );

  return (
    <Page title='Batch Failures' pageType='Batch Failures' isLoading={isLoading}>
      <TablePage tableClassName='batch-failures-table'>
        <BPMTable
          // Keyed on load state: `defaultTokens.advanced`'s "Restartable is false" clause is
          // an exact-match ('is') default token, which needs to resolve against real,
          // populated filter options -- captured only once against BPMTable's initial mount.
          // Forcing a remount once real data lands avoids capturing that default against the
          // empty pre-load options list.
          key={failures ? 'loaded' : 'loading'}
          defaultAdvancedFilter
          defaultTokens={{ advanced: advancedFilterTokens }}
          onTokensChange={handleTokensChange}
          noRowsRenderer={renderTableLoading}
          data={failures ?? []}
          headers={isAdmin || isNOC ? [restartHeader, ...headers] : headers}
        />
      </TablePage>
    </Page>
  );
};

export { BatchFailures };
